import { getPillarDisplayName } from '../constants/awardCategoryPillar';
import * as XLSX from 'xlsx';
import { apiRequest } from './apiConfig';

/**
 * Fetch all award roles and create email-to-pillar mapping
 * @returns {Promise<Object>} Object mapping judge emails to their pillars
 */
const fetchJudgePillarMapping = async () => {
  try {
    let allRoles = [];
    let page = 1;
    let hasMorePages = true;
    const pageSize = 100;

    while (hasMorePages) {
      const response = await apiRequest(
        `/award-roles?pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true`,
        {
          method: 'GET',
        }
      );

      const result = await response.json();

      if (!result.data || !Array.isArray(result.data)) {
        break;
      }

      allRoles = allRoles.concat(result.data);

      const pagination = result.meta?.pagination;
      if (pagination) {
        hasMorePages = page < pagination.pageCount;
      } else {
        hasMorePages = false;
      }

      page++;
    }

    // Create email-to-pillar mapping (case-insensitive) with display name transformation
    const emailToPillar = {};
    allRoles.forEach(role => {
      if (role.email && role.pillar) {
        emailToPillar[role.email.toLowerCase()] = getPillarDisplayName(
          role.pillar
        );
      }
    });

    return emailToPillar;
  } catch (error) {
    console.error('Error fetching judge pillar mapping:', error);
    return {};
  }
};

/**
 * Calculate individual judge rank for a specific judgment
 * @param {Object} judgment - The specific judgment object
 * @param {Object} submission - The submission object
 * @param {Array} allSubmissions - All submissions for ranking context
 */
const calculateIndividualJudgeRank = (judgment, submission, allSubmissions) => {
  const judgeEmail = judgment.judge_email;

  // Get all submissions evaluated by this judge IN THE SAME CATEGORY
  const judgeEvaluations = [];

  allSubmissions.forEach(sub => {
    // Only include submissions from the same category
    if (sub.category === submission.category) {
      const subJudgments = sub.all_judgments || [];
      subJudgments.forEach(subJudgment => {
        if (subJudgment.judge_email === judgeEmail) {
          const judgmentScore =
            subJudgment.calculated_weighted_score ||
            subJudgment.weighted_total_score ||
            0;
          judgeEvaluations.push({
            submissionId: sub.id,
            score: judgmentScore,
            isCurrentSubmission: sub.id === submission.id,
          });
        }
      });
    }
  });

  // Sort evaluations by score (descending) to determine rankings
  judgeEvaluations.sort((a, b) => b.score - a.score);

  // Calculate standard competition ranking (handles ties properly)
  let currentSubmissionRank = 0;
  let currentRank = 1;

  for (let i = 0; i < judgeEvaluations.length; i++) {
    // If this is not the first submission and score is different from previous, update rank
    if (i > 0 && judgeEvaluations[i].score !== judgeEvaluations[i - 1].score) {
      currentRank = i + 1; // Set rank to current position + 1
    }

    // If this is the current submission, record its rank
    if (judgeEvaluations[i].isCurrentSubmission) {
      currentSubmissionRank = currentRank;
      break;
    }
  }

  return currentSubmissionRank > 0
    ? `${currentSubmissionRank} of ${judgeEvaluations.length}`
    : 'N/A';
};

/**
 * Calculate average rank for a submission across all judges (same logic as UI)
 * @param {Object} submission - The submission object
 * @param {Array} allSubmissions - All submissions for ranking context
 */
const calculateAverageRankForExport = (submission, allSubmissions) => {
  const allJuryEvaluations = submission.all_judgments || [];

  if (allJuryEvaluations.length === 0) {
    return 'N/A';
  }

  const judgeRanks = [];

  allJuryEvaluations.forEach(evaluation => {
    const judgeEmail = evaluation.judge_email;

    // Get all submissions evaluated by this judge IN THE SAME CATEGORY
    const judgeEvaluations = [];

    allSubmissions.forEach(sub => {
      // Only include submissions from the same category
      if (sub.category === submission.category) {
        const subJudgments = sub.all_judgments || [];
        subJudgments.forEach(judgment => {
          if (judgment.judge_email === judgeEmail) {
            const judgmentScore =
              judgment.calculated_weighted_score ||
              judgment.weighted_total_score ||
              0;
            judgeEvaluations.push({
              submissionId: sub.id,
              score: judgmentScore,
              isCurrentSubmission: sub.id === submission.id,
              category: sub.category,
            });
          }
        });
      }
    });

    // Sort evaluations by score (descending) to determine rankings
    judgeEvaluations.sort((a, b) => b.score - a.score);

    // Calculate standard competition ranking (handles ties properly)
    let currentSubmissionRank = 0;
    let currentRank = 1;

    for (let i = 0; i < judgeEvaluations.length; i++) {
      // If this is not the first submission and score is different from previous, update rank
      if (
        i > 0 &&
        judgeEvaluations[i].score !== judgeEvaluations[i - 1].score
      ) {
        currentRank = i + 1; // Set rank to current position + 1
      }

      // If this is the current submission, record its rank
      if (judgeEvaluations[i].isCurrentSubmission) {
        currentSubmissionRank = currentRank;
        break;
      }
    }

    if (currentSubmissionRank > 0) {
      judgeRanks.push(currentSubmissionRank);
    }
  });

  if (judgeRanks.length === 0) {
    return 'N/A';
  }

  // Calculate average rank
  const averageRank =
    judgeRanks.reduce((sum, rank) => sum + rank, 0) / judgeRanks.length;
  return averageRank.toFixed(2);
};

/**
 * Export qualification judgment data to Excel with 2 sheets
 * @param {Array} submissions - Array of submission objects with judgment data
 * @param {string} filename - Optional filename for the export
 */
export const exportQualificationJudgmentToExcel = async (
  submissions,
  filename = 'qualification-judgment-results'
) => {
  if (!submissions || submissions.length === 0) {
    throw new Error('No data to export');
  }

  // Fetch judge pillar mapping
  const judgePillarMapping = await fetchJudgePillarMapping();

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // SHEET 1: Final Results (Score & Avg Rank)
  const finalResultsData = submissions.map(submission => {
    const allJudgments = submission.all_judgments || [];
    const validJudgments = allJudgments.filter(j => j.weighted_total_score > 0);

    // For Women in Tech Award and Young Digital Leader Award, use submitter name as description
    let description =
      submission.project_name ||
      submission.title ||
      submission.full_name ||
      'N/A';
    if (
      submission.category === 'Women in Tech Award' ||
      submission.category === 'Young Digital Leader Award'
    ) {
      description = submission.full_name || 'N/A';
    }

    return {
      Category: submission.category || 'N/A',
      Pillar: submission.pillar || 'N/A',
      Description: description,
      'Submitter Name': submission.full_name || 'N/A',
      Email: submission.email || 'N/A',
      'Final Score': submission.total_score
        ? submission.total_score.toFixed(2)
        : 'N/A',
      'Average Rank': calculateAverageRankForExport(submission, submissions),
      'Number of Judges': validJudgments.length,
      Status: validJudgments.length > 0 ? 'Evaluated' : 'Pending',
    };
  });

  const finalResultsWS = XLSX.utils.json_to_sheet(finalResultsData);
  XLSX.utils.book_append_sheet(workbook, finalResultsWS, 'Final Results');

  // SHEET 2: Raw Judgment Evaluation Data
  const rawJudgmentData = [];

  submissions.forEach(submission => {
    const allJudgments = submission.all_judgments || [];

    // For Women in Tech Award and Young Digital Leader Award, use submitter name as description
    let description =
      submission.project_name ||
      submission.title ||
      submission.full_name ||
      'N/A';
    if (
      submission.category === 'Women in Tech Award' ||
      submission.category === 'Young Digital Leader Award'
    ) {
      description = submission.full_name || 'N/A';
    }

    if (allJudgments.length === 0) {
      // Add row for submissions without judgments
      rawJudgmentData.push({
        Category: submission.category || 'N/A',
        Pillar: submission.pillar || 'N/A',
        Description: description,
        'Submitter Name': submission.full_name || 'N/A',
        Email: submission.email || 'N/A',
        'Judge Name': 'No Evaluation',
        'Judge Email': 'N/A',
        'Judge Pillar': 'N/A',
        'Weighted Score': 'N/A',
        'Judge Rank': 'N/A',
        'Evaluation Date': 'N/A',
        Status: 'Pending',
        'Aspect Details': 'No evaluation data',
      });
    } else {
      // Add row for each judge evaluation with detailed aspect information
      allJudgments.forEach(judgment => {
        const aspectScores = judgment.aspect_scores || [];
        const aspectDetails = aspectScores
          .map(
            aspect =>
              `${aspect.aspect_name || aspect.aspect_id}: ${aspect.score || aspect.value} (${aspect.weight || 'N/A'}%)`
          )
          .join(' | ');

        // Calculate individual judge rank for this judgment
        const judgeRank = calculateIndividualJudgeRank(
          judgment,
          submission,
          submissions
        );

        rawJudgmentData.push({
          Category: submission.category || 'N/A',
          Pillar: submission.pillar || 'N/A',
          Description: description,
          'Submitter Name': submission.full_name || 'N/A',
          Email: submission.email || 'N/A',
          'Judge Name': judgment.judge_name || 'N/A',
          'Judge Email': judgment.judge_email || 'N/A',
          'Judge Pillar':
            judgePillarMapping[judgment.judge_email?.toLowerCase()] || 'N/A',
          'Weighted Score': judgment.weighted_total_score
            ? judgment.weighted_total_score.toFixed(2)
            : 'N/A',
          'Judge Rank': judgeRank,
          'Evaluation Date': judgment.judged_at
            ? new Date(judgment.judged_at).toLocaleDateString()
            : 'N/A',
          Status: 'Evaluated',
          'Aspect Details': aspectDetails || 'No aspect details',
        });
      });
    }
  });

  const rawJudgmentWS = XLSX.utils.json_to_sheet(rawJudgmentData);
  XLSX.utils.book_append_sheet(workbook, rawJudgmentWS, 'Raw Judgment Data');

  // Generate timestamp for filename
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const finalFilename = `${filename}-${timestamp}.xlsx`;

  // Write and download file
  XLSX.writeFile(workbook, finalFilename);

  return finalFilename;
};

/**
 * Export final judgment data to Excel with 2 sheets
 * @param {Array} submissions - Array of final submission objects with judgment data
 * @param {string} filename - Optional filename for the export
 */
export const exportFinalJudgmentToExcel = async (
  submissions,
  filename = 'final-judgment-results'
) => {
  if (!submissions || submissions.length === 0) {
    throw new Error('No data to export');
  }

  // Fetch judge pillar mapping
  const judgePillarMapping = await fetchJudgePillarMapping();

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // SHEET 1: Final Results (Score & Avg Rank)
  const finalResultsData = submissions.map(submission => {
    const allJudgments = submission.all_judgments || [];
    const validJudgments = allJudgments.filter(j => j.weighted_total_score > 0);

    // For Women in Tech Award and Young Digital Leader Award, use submitter name as description
    let description =
      submission.project_name ||
      submission.title ||
      submission.full_name ||
      'N/A';
    if (
      submission.category === 'Women in Tech Award' ||
      submission.category === 'Young Digital Leader Award'
    ) {
      description = submission.full_name || 'N/A';
    }

    return {
      Category: submission.category || 'N/A',
      Pillar: submission.pillar || 'N/A',
      Description: description,
      'Submitter Name': submission.full_name || 'N/A',
      Email: submission.email || 'N/A',
      'Final Score': submission.total_score
        ? submission.total_score.toFixed(2)
        : 'N/A',
      'Average Rank': calculateAverageRankForExport(submission, submissions),
      'Number of Judges': validJudgments.length,
      Status: validJudgments.length > 0 ? 'Evaluated' : 'Pending',
      'Competition Stage': 'Final',
    };
  });

  const finalResultsWS = XLSX.utils.json_to_sheet(finalResultsData);
  XLSX.utils.book_append_sheet(workbook, finalResultsWS, 'Final Results');

  // SHEET 2: Raw Judgment Evaluation Data
  const rawJudgmentData = [];

  submissions.forEach(submission => {
    const allJudgments = submission.all_judgments || [];

    // For Women in Tech Award and Young Digital Leader Award, use submitter name as description
    let description =
      submission.project_name ||
      submission.title ||
      submission.full_name ||
      'N/A';
    if (
      submission.category === 'Women in Tech Award' ||
      submission.category === 'Young Digital Leader Award'
    ) {
      description = submission.full_name || 'N/A';
    }

    if (allJudgments.length === 0) {
      // Add row for submissions without judgments
      rawJudgmentData.push({
        Category: submission.category || 'N/A',
        Pillar: submission.pillar || 'N/A',
        Description: description,
        'Submitter Name': submission.full_name || 'N/A',
        Email: submission.email || 'N/A',
        'Judge Name': 'No Evaluation',
        'Judge Email': 'N/A',
        'Judge Pillar': 'N/A',
        'Weighted Score': 'N/A',
        'Judge Rank': 'N/A',
        'Evaluation Date': 'N/A',
        Status: 'Pending',
        'Competition Stage': 'Final',
        'Aspect Details': 'No evaluation data',
      });
    } else {
      // Add row for each judge evaluation with detailed aspect information
      allJudgments.forEach(judgment => {
        const aspectScores = judgment.aspect_scores || [];
        const aspectDetails = aspectScores
          .map(
            aspect =>
              `${aspect.aspect_name || aspect.aspect_id}: ${aspect.score || aspect.value} (${aspect.weight || 'N/A'}%)`
          )
          .join(' | ');

        // Calculate individual judge rank for this judgment
        const judgeRank = calculateIndividualJudgeRank(
          judgment,
          submission,
          submissions
        );

        rawJudgmentData.push({
          Category: submission.category || 'N/A',
          Pillar: submission.pillar || 'N/A',
          Description: description,
          'Submitter Name': submission.full_name || 'N/A',
          Email: submission.email || 'N/A',
          'Judge Name': judgment.judge_name || 'N/A',
          'Judge Email': judgment.judge_email || 'N/A',
          'Judge Pillar':
            judgePillarMapping[judgment.judge_email?.toLowerCase()] || 'N/A',
          'Weighted Score': judgment.weighted_total_score
            ? judgment.weighted_total_score.toFixed(2)
            : 'N/A',
          'Judge Rank': judgeRank,
          'Evaluation Date': judgment.judged_at
            ? new Date(judgment.judged_at).toLocaleDateString()
            : 'N/A',
          Status: 'Evaluated',
          'Competition Stage': 'Final',
          'Aspect Details': aspectDetails || 'No aspect details',
        });
      });
    }
  });

  const rawJudgmentWS = XLSX.utils.json_to_sheet(rawJudgmentData);
  XLSX.utils.book_append_sheet(workbook, rawJudgmentWS, 'Raw Judgment Data');

  // Generate timestamp for filename
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const finalFilename = `${filename}-${timestamp}.xlsx`;

  // Write and download file
  XLSX.writeFile(workbook, finalFilename);

  return finalFilename;
};

/**
 * Export statistics data to Excel
 * @param {Object} stats - Statistics object
 * @param {string} filename - Optional filename for the export
 */
export const exportStatisticsToExcel = (
  stats,
  filename = 'qualification-statistics'
) => {
  if (!stats) {
    throw new Error('No statistics data to export');
  }

  const workbook = XLSX.utils.book_new();

  // Prepare statistics data
  const statsData = [
    { Metric: 'Total Submissions', Value: stats.total || 0 },
    { Metric: 'Evaluated Submissions', Value: stats.evaluated || 0 },
    { Metric: 'Pending Submissions', Value: stats.pending || 0 },
    {
      Metric: 'Average Score',
      Value: stats.averageScore ? stats.averageScore.toFixed(2) : 'N/A',
    },
    { Metric: 'Total Categories', Value: stats.totalCategories || 0 },
    { Metric: 'Total Judges', Value: stats.totalJudges || 0 },
  ];

  const statsWS = XLSX.utils.json_to_sheet(statsData);
  XLSX.utils.book_append_sheet(workbook, statsWS, 'Statistics');

  // Generate timestamp for filename
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const finalFilename = `${filename}-${timestamp}.xlsx`;

  // Write and download file
  XLSX.writeFile(workbook, finalFilename);

  return finalFilename;
};

/**
 * Export SMDD judgment data to Excel with 2 sheets
 * @param {Array} submissions - Array of SMDD submission objects with judgment data
 * @param {string} filename - Optional filename for the export
 */
export const exportSMDDJudgmentToExcel = (
  submissions,
  filename = 'smdd-judgment-results'
) => {
  if (!submissions || submissions.length === 0) {
    throw new Error('No data to export');
  }

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // SHEET 1: Final Results (Score & Avg Rank)
  const finalResultsData = submissions.map(submission => {
    const allJudgments = submission.all_judgments || [];
    const validJudgments = allJudgments.filter(j => j.weighted_total_score > 0);

    // Extract additional information from detailed_fields
    const detailedFields = submission.detailed_fields || {};
    const additionalInfo = {};

    // Add all detailed fields as separate columns with full text
    Object.entries(detailedFields).forEach(([key, value]) => {
      // Keep full text in Excel export - no truncation
      additionalInfo[key] = value || 'N/A';
    });

    return {
      Description:
        submission.project_name ||
        submission.title ||
        submission.description ||
        'N/A',
      'Submitter Name': submission.full_name || 'N/A',
      Email: submission.email || 'N/A',
      Category: submission.category || 'N/A',
      Pillar: submission.pillar || 'N/A',
      Organization: submission.org_unit || 'N/A',
      'Final Score': submission.total_score
        ? submission.total_score.toFixed(2)
        : 'N/A',
      'Average Rank': calculateAverageRankForExport(submission, submissions),
      'Number of Judges': validJudgments.length,
      Status: validJudgments.length > 0 ? 'Evaluated' : 'Pending',
      ...additionalInfo,
    };
  });

  const finalResultsWS = XLSX.utils.json_to_sheet(finalResultsData);
  XLSX.utils.book_append_sheet(workbook, finalResultsWS, 'Final Results');

  // SHEET 2: Raw Judgment Evaluation Data
  const rawJudgmentData = [];

  submissions.forEach(submission => {
    const allJudgments = submission.all_judgments || [];

    // Extract additional information from detailed_fields for this submission
    const detailedFields = submission.detailed_fields || {};
    const additionalInfo = {};

    // Add all detailed fields as separate columns with full text
    Object.entries(detailedFields).forEach(([key, value]) => {
      // Keep full text in Excel export - no truncation
      additionalInfo[key] = value || 'N/A';
    });

    if (allJudgments.length === 0) {
      // Add row for submissions without judgments
      rawJudgmentData.push({
        Description:
          submission.project_name ||
          submission.title ||
          submission.description ||
          'N/A',
        'Submitter Name': submission.full_name || 'N/A',
        Email: submission.email || 'N/A',
        Category: submission.category || 'N/A',
        Pillar: submission.pillar || 'N/A',
        Organization: submission.org_unit || 'N/A',
        'Judge Name': 'No Evaluation',
        'Judge Email': 'N/A',
        'Weighted Score': 'N/A',
        'Judge Rank': 'N/A',
        'Evaluation Date': 'N/A',
        Status: 'Pending',
        'Aspect Details': 'No evaluation data',
        ...additionalInfo,
      });
    } else {
      // Add row for each judge evaluation with detailed aspect information
      allJudgments.forEach(judgment => {
        const aspectScores = judgment.aspect_scores || [];
        const aspectDetails = aspectScores
          .map(
            aspect =>
              `${aspect.aspect_name || aspect.aspect_id}: ${aspect.score || aspect.value} (${aspect.weight || 'N/A'}%)`
          )
          .join(' | ');

        // Calculate individual judge rank for this judgment
        const judgeRank = calculateIndividualJudgeRank(
          judgment,
          submission,
          submissions
        );

        rawJudgmentData.push({
          Description:
            submission.project_name ||
            submission.title ||
            submission.description ||
            'N/A',
          'Submitter Name': submission.full_name || 'N/A',
          Email: submission.email || 'N/A',
          Category: submission.category || 'N/A',
          Pillar: submission.pillar || 'N/A',
          Organization: submission.org_unit || 'N/A',
          'Judge Name': judgment.judge_name || 'N/A',
          'Judge Email': judgment.judge_email || 'N/A',
          'Weighted Score': judgment.weighted_total_score
            ? judgment.weighted_total_score.toFixed(2)
            : 'N/A',
          'Judge Rank': judgeRank,
          'Evaluation Date': judgment.judged_at
            ? new Date(judgment.judged_at).toLocaleDateString()
            : 'N/A',
          Status: 'Evaluated',
          'Aspect Details': aspectDetails || 'No aspect details',
          ...additionalInfo,
        });
      });
    }
  });

  const rawJudgmentWS = XLSX.utils.json_to_sheet(rawJudgmentData);
  XLSX.utils.book_append_sheet(workbook, rawJudgmentWS, 'Raw Judgment Data');

  // Generate timestamp for filename
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const finalFilename = `${filename}-${timestamp}.xlsx`;

  // Write and download file
  XLSX.writeFile(workbook, finalFilename);

  return finalFilename;
};
